import React, { useState } from 'react';

import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';

import {
  Box,
  Button,
  Card,
  CardActionArea,
  Collapse,
  Container,
  Grid,
  Typography,
} from '@mui/material';

import Rating from '@mui/material/Rating';

import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StarRateIcon from '@mui/icons-material/StarRate';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import * as Yup from 'yup';

import Error from '../_error';
import TopNav from '../../components/TopNav';
import BottomNav from '../../components/BottomNav';

import ReviewCard from '../../components/business/ReviewCard';
import Link from '../../components/Link';

import Onboard from '../../components/auth/Onboard';
import gaEvent from '../../lib/gaEvent';

import axiosInstance from '../../lib/axiosInstance';
import useAuth from '../../contexts/auth';


const BusinessReview = ({ business, statusCode, recentReviews, slug }) => {
  const router = useRouter();
  const { isAuthenticated } = useAuth();

  const [hover, setHover] = useState(-1);
  const [authOpened, setAuthOpened] = React.useState(false);

  const openAuth = () => {
    setAuthOpened(true);
  };

  const closeAuth = (event, reason, sent) => {
    if (sent) return;
    setAuthOpened(false);
    gaEvent({
      category: 'Write Review',
      action: 'Auth Closed',
      label: 'Auth Closed',
    });
  };

  if (statusCode || recentReviews.statusCode) {
    return <Error statusCode={statusCode || recentReviews.statusCode} />;
  }

  const ReviewSchema = Yup.object().shape({
    rating: Yup.number().min(0.5).required('Please select a rating'),
    best_attribute: Yup.string(),
    body: Yup.string(),
    email: Yup.string()
  });

  const ratingLabels = {
    1: 'Terrible',
    2: 'Bad',
    3: 'Average',
    4: 'Good',
    5: 'Excellent',
  };

  return <>
    <Head>
      <title>Review {business.name} | MechanicBuddy</title>
      <meta
        name="description"
        content={`Help others by providing honest feedback. Write a review of ${business.name}`} />

      <link
        rel="canonical"
        href={`https://www.mechanicbuddy.co.za/write-review/${slug}/`}
      />
    </Head>

    <Box display="flex" flexDirection="column" minHeight="100vh">
      <TopNav />

      <Container
        maxWidth="lg"
        sx={{
          py: {
            xs: 3,
            md: 6,
          },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Grid
          container
          columnSpacing={6}
        >
          <Grid
            item
            xs={12}
          >
            <Typography
              variant="h3"
              component="h1"
              color="primary.light"
            >
              Write a Review
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={7}
          >
            <Box height={32} mb={3} mt={1}>
              <Typography variant="body1" component="h2" color="text.secondary">
                By sharing your experiences, you&apos;re helping others make better choices
              </Typography>
            </Box>
            <Card
              variant="outlined"
              sx={{
                p: {
                  xs: 1.5,
                  sm: 3,
                },
              }}>
                <Box sx={{
                  display: 'flex',
                  mb: 4
                }}>
                  <Box
                    sx={{
                      minWidth: 120,
                      minHeight: 120,
                      display: {
                        xs: "none",
                        md: "block",
                      },
                      mr: 3,
                      position: "relative",
                    }}
                  >
                    <Image
                      src={
                        business.logo
                          ? business.logo + '?w=120'
                          : '/img/profile-placeholderx2.jpg'
                      }
                      title={business.name}
                      alt={business.name}
                      layout="fill"
                      objectFit="cover"
                      quality={100}
                    />
                  </Box>
                  <Box overflow="hidden" width="100%">
                    <Box display="flex" alignItems="center">
                      <Link
                        style={{ textDecoration: 'none' }}
                        href="/mechanic/[slug]"
                        as={`/mechanic/${slug}`}
                      >
                        <Typography variant="h5" component="h3">
                          {business.name}
                        </Typography>
                      </Link>
                      {business.registered && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            ml: 1.5,
                            color: 'primary.light'
                          }}
                        >
                          <VerifiedUserIcon fontSize="small" color="inherit" />
                        </Box>
                      )}
                    </Box>
                    <Box display="flex" alignItems="center">
                      <LocationOnIcon color="disabled" fontSize="small" />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontStyle: 'italic',
                          fontWeight: 300,
                          mt: 0.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width: '100%',
                        }}
                      >
                        {business.address}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <StarRateIcon color="secondary" />
                      {business.total_ratings > 0 ? (
                        <Typography variant="subtitle1">
                          {business.rating}{' '}
                          <Typography component="span" color="textSecondary">
                            ({business.total_ratings})
                          </Typography>
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No ratings yet
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box>
                  <Formik
                    initialValues={{
                      rating: 0,
                      body: '',
                      best_attribute: '',
                      email: '',
                    }}
                    validationSchema={ReviewSchema}
                    enableReinitialize
                    onSubmit={(values, { setSubmitting }) => {
                      console.log(values);

                      axiosInstance.post(`/api/v2/create-new-review/${business.id}/`, values)
                        .then(() => {
                          setSubmitting(false);
                          gaEvent({
                            category: 'Write Review',
                            action: 'Review Created',
                            label: 'Review Created',
                          });
                          if (!values.email) {
                            router.push('/dash/');
                          }
                        })
                        .catch(error => {
                          setSubmitting(false);
                          gaEvent({
                            category: 'Write Review',
                            action: 'Review Failed',
                            label: 'Review Failed',
                          });
                          console.log('error', error);
                        });
                    }}
                  >
                    {({ values, setFieldValue, isSubmitting, dirty, isValid, submitForm }) => (
                      <Form>
                        <Box
                          display="flex"
                          flexDirection={{ xs: 'column-reverse', sm: 'row' }}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                        >
                          <Rating
                            name="rating"
                            value={values.rating}
                            onChange={(event, value) => {
                              setFieldValue('rating', value || 0);
                              setHover(value || -1);
                            }}
                            onChangeActive={(event, newHover) => {
                              setHover(newHover);
                            }}
                            sx={{ fontSize: 40 }}
                          />
                          <Box
                            sx={{
                              ml: {
                                xs: 0.5,
                                sm: 2
                              },
                              mb: {
                                xs: 0.5,
                                sm: 0
                              },
                              color: 'primary.light'
                            }}
                          >
                            {hover !== -1 ?
                              ratingLabels[hover] :
                              values.rating > 0 ? ratingLabels[values.rating] :
                              'Rate their service'
                            }
                          </Box>
                        </Box>

                        <Collapse in={!!values.rating} timeout="auto" unmountOnExit>
                          <Field
                            component={TextField}
                            name="body"
                            label="Your Review"
                            placeholder="Tell us about your experience. Your review helps others make better decisions."
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            autoComplete="off"
                            multiline
                            rows={5}
                          />

                          <Typography
                            color="text.secondary"
                            sx={{ mt: 2 }}
                          >Best attribute</Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >Which of the following attributes best describe them</Typography>

                          <Grid
                            container
                            spacing={2}
                          >
                            <Grid item xs={6} md={3}>
                              <Card
                                variant="outlined"
                                sx={{
                                  borderColor: values.best_attribute === 'clean' && 'primary.light' 
                                }}
                              >
                                <CardActionArea
                                  sx={{
                                    p: 1,
                                    display: 'flex',
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                  onClick={() => {
                                    const newValue = values.best_attribute === 'clean' ? '' : 'clean'
                                    setFieldValue('best_attribute', newValue)
                                  }}
                                >
                                  <Image
                                    src={`/icons/reviews-thumbnail${values.best_attribute !== 'clean' ? '-grey.png' : '.svg' }`}
                                    height={80}
                                    width={80}
                                    alt="Clean and tidy"
                                    style={{
                                      filter: 'grayscale(100%)'
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    textAlign="center"
                                  >Clean</Typography>
                                </CardActionArea>
                              </Card>
                            </Grid>

                            <Grid item xs={6} md={3}>
                              <Card
                                variant="outlined"
                                sx={{
                                  borderColor: values.best_attribute === 'friendly' && 'primary.light'
                                }}
                              >
                                <CardActionArea
                                  sx={{
                                    p: 1,
                                    display: 'flex',
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                  onClick={() => {
                                    const newValue = values.best_attribute === 'friendly' ? '' : 'friendly'
                                    setFieldValue('best_attribute', newValue)
                                  }}
                                >
                                  <Image
                                    src={`/icons/choose-a-mechanic${values.best_attribute !== 'friendly' ? '-grey.png' : '.svg' }`}
                                    height={80}
                                    width={80}
                                    alt="Friendly"
                                  />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    textAlign="center"
                                  >Friendly</Typography>
                                </CardActionArea>
                              </Card>
                            </Grid>

                            <Grid item xs={6} md={3}>
                              <Card
                                variant="outlined"
                                sx={{
                                  borderColor: values.best_attribute === 'skill' && 'primary.light' 
                                }}
                              >
                                <CardActionArea
                                  sx={{
                                    p: 1,
                                    display: 'flex',
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                  onClick={() => {
                                    const newValue = values.best_attribute === 'skill' ? '' : 'skill'
                                    setFieldValue('best_attribute', newValue)
                                  }}
                                >
                                  <Image
                                    src={`/icons/choose-a-service${values.best_attribute !== 'skill' ? '-grey.png' : '.svg' }`}
                                    height={80}
                                    width={80}
                                    alt="Clean and tidy"
                                  />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    textAlign="center"
                                  >Skillful</Typography>
                                </CardActionArea>
                              </Card>
                            </Grid>

                            <Grid item xs={6} md={3}>
                              <Card
                                variant="outlined"
                                sx={{
                                  borderColor: values.best_attribute === 'value' && 'primary.light' 
                                }}
                              >
                                <CardActionArea
                                  sx={{
                                    p: 1,
                                    display: 'flex',
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                  onClick={() => {
                                    const newValue = values.best_attribute === 'value' ? '' : 'value'
                                    setFieldValue('best_attribute', newValue)
                                  }}
                                >
                                  <Image
                                    src={`/icons/post-requests${values.best_attribute !== 'value' ? '-grey.png' : '.svg' }`}
                                    height={80}
                                    width={80}
                                    alt="Affordable"
                                  />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    textAlign="center"
                                  >Affordable</Typography>
                                </CardActionArea>
                              </Card>
                            </Grid>
                          </Grid>

                          <Button
                            variant="contained"
                            color="secondary"
                            disabled={!(isValid && dirty) || isSubmitting}
                            sx={{ mt: 1 }}
                            onClick={() => {
                              gaEvent({
                                category: 'Write Review',
                                action: 'Click Submit',
                                label: 'Submit Review',
                              });
                              if (isAuthenticated) {
                                submitForm();
                              } else {
                                openAuth();
                              }
                            }}
                          >
                            Submit
                          </Button>

                        </Collapse>

                        <Onboard 
                          open={authOpened} 
                          handleClose={closeAuth}
                          setFieldValue={setFieldValue}
                          submitForm={submitForm}
                        />
                      </Form>
                    )}
                  </Formik>
                </Box>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={5}
            sx={{
              height: {
                xs: '100%',
                md: 620,
              },
              overflowY: 'auto'
            }}
          >
            <Box height={{ xs: 'unset', md: 32 }} mb={{ xs: 2, md: 3 }} mt={{ xs: 4, md: 1 }}>
              <Typography variant="body1" component="h3" color="primary.main">
                Recent Reviews{business.reviews.length === 0 && ' on MechanicBuddy'}
              </Typography>
            </Box>
            <Card
              variant="outlined"
              sx={{
                my: {
                  xs: 1.5,
                  sm: 3
                },
                px: {
                  xs: 1.5,
                  sm: 3
                }
              }}
            >
              {business.reviews.length > 0
                ? business.reviews.map((review, index, arr) => (
                    <ReviewCard
                      key={review.id}
                      review={review}
                      divide={index != arr.length - 1}
                    />
                  ))
                : recentReviews.data.results.map((review, index, arr) => (
                    <ReviewCard
                      key={review.id}
                      review={review}
                      divide={index != arr.length - 1}
                      showWorkshop
                    />
                  ))}
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
    <BottomNav />
  </>;
};

export async function getServerSideProps({ params, res }) {
  const data = await axiosInstance(
    `/content/business/${params.slug}/`
  )
    .then(result => {
      return {
        business: result.data,
        statusCode: null,
      };
    })
    .catch(error => {
      return {
        business: null,
        statusCode: error.response.status,
      };
    });
  const recentReviews = await axiosInstance(`/content/recent-reviews/`)
    .then(result => {
      return {
        data: result.data,
        statusCode: null,
      };
    })
    .catch(error => {
      return {
        data: null,
        statusCode: error.response.status,
      };
    });

  if (data.statusCode === 404 || recentReviews.statusCode === 404) {
    return {
      notFound: true
    }
  }

  const props = {
    business: data.business,
    statusCode: data.statusCode,
    recentReviews,
    slug: params.slug
  };

  return { props };
}

export default BusinessReview;
